/** @jsx jsx */
/* eslint-disable max-len, no-nested-ternary */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { css, jsx } from '@emotion/core';
import { useHistory, useParams } from 'react-router-dom';
import SidebarTemplate from 'components/templates/SidebarTemplate';
import BlockUI from 'components/molecules/BlockUi';
import Icon from 'components/atoms/Icon';
import useToastNotification from 'hooks/useToastNotification';
import {
  confirmShifts, downloadCsv, getRecalculateStatus, recalculateLaborCost, unconfirmShifts,
} from 'api/shiftBoard';
import {
  BoardSetting, DayHeader, GeneratedOperations, Operation, PatternInfo, PrintSetting, RecalculateResult, SimulationShift, StaffInfo,
} from 'domain/attend/shiftBoard/types';
import {
  minutesToHm, shortDateLabel, spanMinutes, workMinutesOf,
} from 'domain/attend/shiftBoard/time';
import { textColorFor } from 'domain/attend/shiftBoard/print';
import Toolbar from 'components/organismos/master/attend/ShiftBoard/Toolbar';
import AlertBanners from 'components/organismos/master/attend/ShiftBoard/AlertBanners';
import SimulationBar from 'components/organismos/master/attend/ShiftBoard/SimulationBar';
import PatternPalette, { FreeTimes, PaletteSelection, PatternColor } from 'components/organismos/master/attend/ShiftBoard/PatternPalette';
import BoardGrid, { HopeLabel } from 'components/organismos/master/attend/ShiftBoard/BoardGrid';
import DayTimeline from 'components/organismos/master/attend/ShiftBoard/DayTimeline';
import CellEditModal, { ApplicationKind } from 'components/organismos/master/attend/ShiftBoard/CellEditModal';
import ConfirmModal from 'components/organismos/master/attend/ShiftBoard/ConfirmModal';
import UnconfirmDialog from 'components/organismos/master/attend/ShiftBoard/UnconfirmDialog';
import AutoGenerateModal from 'components/organismos/master/attend/ShiftBoard/AutoGenerateModal';
import CopyModal from 'components/organismos/master/attend/ShiftBoard/CopyModal';
import SupportStaffModal from 'components/organismos/master/attend/ShiftBoard/SupportStaffModal';
import BoardSettingsModal from 'components/organismos/master/attend/ShiftBoard/BoardSettingsModal';
import PrintSettingsModal from 'components/organismos/master/attend/ShiftBoard/PrintSettingsModal';
import RequiredStaffPanel from 'components/organismos/master/attend/ShiftBoard/RequiredStaffPanel';
import StaffDetailPanel from 'components/organismos/master/attend/ShiftBoard/StaffDetailPanel';
import { MOCK_PATTERN_COLORS, mock, mockButton } from 'components/organismos/master/attend/ShiftBoard/styles';
import useShiftBoard, { describeError } from './useShiftBoard';

type ModalKind = 'cell' | 'confirm' | 'unconfirm' | 'autoGenerate' | 'copy' | 'support' | 'settings' | 'print' | 'required' | 'staff' | null;

const APPLICATION_PATH: { [k in ApplicationKind]: string } = {
  stamp: '/shiftBoard/attendStampModificationApplication',
  holiday: '/shiftBoard/attendHolidayApplication',
  overtime: '/shiftBoard/attendOvertimeApplication',
  holidaywork: '/shiftBoard/attendHolidayworkApplication',
  transfer: '/shiftBoard/attendTransferApplication',
};

/** 押してからドラッグを始めるまでに動かす距離（px）。10/5 */
const DRAG_START_PX = 5;

type DragState =
  | { kind: 'palette'; sel: PaletteSelection }
  | { kind: 'chip'; staffCode: string; date: string; index: number; copy: boolean };

const styles = {
  page: css({
    background: '#F5F5F5',
    minHeight: 'calc(100vh - 48px)',
    paddingBottom: '24px',
    // 表示単位を切り替えたときに縦スクロールバーの有無で幅が動かないようにする（9/16 決定）
    scrollbarGutter: 'stable',
  }),
  // パターンの帯を表の上に置く（9/24 案D。以前は左に縦長で置いていた）
  body: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    margin: '8px 16px',
    alignItems: 'stretch',
    minWidth: 0,
    // 日ビューなど行数が少ない表示に切り替えても、ページの高さが縮んで画面が跳ねないようにする
    minHeight: '60vh',
  }),
  ghost: css({
    position: 'fixed', zIndex: 300, pointerEvents: 'none', padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 700, boxShadow: '0 2px 8px rgba(0,0,0,.25)', opacity: 0, left: '-9999px',
  }),
  toggles: css({
    margin: '8px 16px 0', display: 'flex', gap: '16px', fontSize: '12px', color: mock.textSub, alignItems: 'center',
  }),
  // 確定後に走る人件費再計算の進捗バー（9/18 決定）
  recalcBar: css({
    display: 'inline-block', width: '160px', height: '6px', borderRadius: '9999px', background: mock.gray10, overflow: 'hidden',
  }),
  recalcBarFill: css({
    display: 'block', height: '100%', background: mock.primary, transition: 'width .3s',
  }),
  // 更新・設定のアイコン。案内文と同じ行の右端に寄せる（9/17 決定）
  rowActions: css({
    marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px', flex: 'none',
  }),
  iconAction: css({
    width: '28px',
    height: '28px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${mock.gray20}`,
    borderRadius: '4px',
    background: '#fff',
    cursor: 'pointer',
    padding: 0,
    '&:hover': { background: mock.primaryM95, borderColor: mock.primaryM80 },
    '&:disabled': { cursor: 'default', opacity: 0.6 },
  }),
};

/** ロック中の日を触ったときの案内。公開済みならロック解除を案内する（9/24） */
const lockMessage = (day: DayHeader): string => (day.lockReason === 'SHIFT_CONFIRMED'
  ? 'この日は公開済みのためロックされています。「ロック解除」を押すと編集できます。'
  : 'この日はロックされています（本部確定済みなど）。');

const autoBreak = (minutes: number): number => (minutes >= 480 ? 60 : minutes >= 360 ? 45 : minutes > 240 ? 15 : 0);

/**
 * シフト編成（新）。モック（hitosuke-shift-mock-demo）のシフト表ページに準拠
 * ルート: /shiftBoard/:orgCode?/:dateFrom?
 */
const ShiftBoardPage: React.FC = () => {
  const params = useParams<{ orgCode?: string; dateFrom?: string }>();
  const history = useHistory();
  const { errorNotification, successNotification } = useToastNotification();
  const ctl = useShiftBoard(params.orgCode, params.dateFrom);
  const {
    board, view, range, orgCode, isSimulating,
  } = ctl;
  // 保存ボタンを押したときの描画や、再計算を待つタイマーから呼ばれる読み直しは、最新の期間の reload を ref から呼ぶ（10/5）
  const reloadRef = useRef(ctl.reload);
  reloadRef.current = ctl.reload;

  const [modal, setModal] = useState<ModalKind>(null);
  // 確定・ロック解除の処理中は画面を止める（二度押しを防ぐ。9/26）
  const [working, setWorking] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selection, setSelection] = useState<PaletteSelection | null>(null);
  const [free, setFree] = useState<FreeTimes>({ start: '10:00', end: '15:00', breakMinutes: null });
  const [cellTarget, setCellTarget] = useState<{ staff: StaffInfo; day: DayHeader; index: number | null; presetStart?: string | null } | null>(null);
  const [staffTarget, setStaffTarget] = useState<string | null>(null);
  const [flashKey, setFlashKey] = useState<string | null>(null);
  // 確定したら人件費の再計算が自動で走る（9/18 決定）。その進み具合をここに持つ
  const [recalcJob, setRecalcJob] = useState<RecalculateResult | null>(null);
  const recalcTimer = useRef<number | null>(null);
  // 再計算が走っている最中に確定し直した日が出たら、終わったあとにもう一度走らせる（9/24）
  const recalcRunning = useRef(false);
  const recalcAgain = useRef(false);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [dropKey, setDropKey] = useState<string | null>(null);
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;
  // 押しただけではドラッグにしない。少し動かしたら始める（10/5。素早いクリックはクリックとして扱う）
  const pressRef = useRef<{ state: DragState; x: number; y: number } | null>(null);
  // ドラッグを終えた直後のクリックを無視する（同じセルの上で離したとき、詳細や割当が走らないように）
  const suppressClickRef = useRef(false);

  const editable = !!(board && board.role.editable);
  const showLaborCost = !!(board && (board.role.canViewLaborCostFulltime || board.role.canViewLaborCostPart));

  // 色: マスタの color、無ければモックの既定色をパターン順に
  const colorOf = useCallback((patternId: string | null): PatternColor => {
    if (!patternId || !board) {
      return { bg: mock.freeBg, fg: mock.freeText };
    }
    const idx = board.shiftPatterns.findIndex((p) => p.shiftPatternId === patternId);
    const p = idx >= 0 ? board.shiftPatterns[idx] : null;
    if (p && p.color) {
      return { bg: p.color, fg: textColorFor(p.color) };
    }
    return MOCK_PATTERN_COLORS[(idx < 0 ? 0 : idx) % MOCK_PATTERN_COLORS.length];
  }, [board]);

  const hopeMap = useMemo(() => {
    const m: { [k: string]: HopeLabel } = {};
    if (board) {
      const patterns: { [id: string]: PatternInfo } = {};
      board.shiftPatterns.forEach((p) => {
        patterns[p.shiftPatternId] = p;
      });
      // 希望は何時から何時かが分かるように時刻で出す（9/24）。パターンの希望はパターンの時刻を使う
      const hm = (t: string | null | undefined) => (t ? t.slice(0, 5).replace(/^0(\d)/, '$1') : null);
      board.hopeShifts.forEach((h) => {
        if (h.type === 2) {
          m[`${h.staffCode}|${h.targetDate}`] = { label: '休み', isOff: true };
          return;
        }
        const p = h.shiftPatternId ? patterns[h.shiftPatternId] : undefined;
        const start = hm(p ? p.start : h.start);
        const end = hm(p ? p.end : h.end);
        m[`${h.staffCode}|${h.targetDate}`] = {
          label: start && end ? `${start}-${end}` : (p ? p.shortName : '出勤'),
          isOff: false,
          patternId: p ? p.shiftPatternId : null,
          start,
          end,
        };
      });
    }
    return m;
  }, [board]);
  const hopeStaff = useMemo(() => {
    const s: { [c: string]: boolean } = {};
    if (board) {
      board.hopeShifts.forEach((h) => {
        s[h.staffCode] = true;
      });
    }
    return s;
  }, [board]);
  // 雇用形態に合わないパターンの割り当ては、止めずに警告だけ出す（9/9 決定）。
  // 9/24 に反映前の確認モーダルがなくなったので、保存したあとの通知で知らせる
  const patternMismatches = useCallback((operations: Array<Operation>): Array<string> => {
    if (!board) {
      return [];
    }
    const staffMap: { [code: string]: StaffInfo } = {};
    board.staffs.forEach((s) => {
      staffMap[s.staffCode] = s;
    });
    const out: Array<string> = [];
    operations.forEach((op) => {
      if (!op.shiftPatternId || (op.attendType !== undefined && op.attendType !== null && op.attendType !== 0)) {
        return;
      }
      const pattern = ctl.patternMap[op.shiftPatternId];
      const staff = staffMap[op.staffCode || ''];
      if (!pattern || !staff || !pattern.employmentId || !staff.employmentId) {
        return;
      }
      if (pattern.employmentId !== staff.employmentId) {
        out.push(`${op.targetDate ? shortDateLabel(op.targetDate) : ''} ${staff.staffName}: 「${pattern.name}」は別の雇用形態のパターンです`);
      }
    });
    return out;
  }, [board, ctl.patternMap]);

  const hopeOf = useCallback((sc: string, d: string): HopeLabel => hopeMap[`${sc}|${d}`] || null, [hopeMap]);
  const staffSubmitted = useCallback((sc: string): boolean | null => (board && board.period.periodId ? !!hopeStaff[sc] : null), [board, hopeStaff]);

  const flash = useCallback((staffCode: string, date: string) => {
    const key = `${staffCode}|${date}`;
    setFlashKey(key);
    window.setTimeout(() => setFlashKey((k) => (k === key ? null : k)), 700);
  }, []);

  const selectionLabel = (sel: PaletteSelection | null): string => {
    if (!sel) {
      return '';
    }
    if (sel.kind === 'pattern') {
      const p = board ? board.shiftPatterns.filter((x) => x.shiftPatternId === sel.patternId)[0] : null;
      return p ? p.name : 'パターン';
    }
    if (sel.kind === 'holiday') {
      return sel.isLegal && board && board.org.distinctionHoliday ? '公休（法定）' : '公休';
    }
    if (sel.kind === 'free') {
      return `${free.start}–${free.end}`;
    }
    return '休み（割当を外す）';
  };

  /** パレットの選択から SimulationShift を作る */
  const shiftFromSelection = useCallback((sel: PaletteSelection, staff: StaffInfo): SimulationShift | null => {
    if (!board) {
      return null;
    }
    if (sel.kind === 'holiday') {
      // 法定／所定を区別しない人の公休は、旧画面と同じく法定で入れる（10/5。所定だとタイムカードの休暇の集計から外れる）
      const legal = staff.distinctionHoliday ? sel.isLegal : true;
      return {
        tempId: `p-${Date.now()}`, attendType: legal ? 1 : 2, isLegal: legal, shiftPatternId: null, start: null, end: null, isEndNextDay: false, breakMinutes: null, businessId: null, workOrgCode: orgCode, memo: '', support: null, workMinutes: 0,
      };
    }
    if (sel.kind === 'free') {
      const nextDay = spanMinutes(free.start, free.end, false) <= 0;
      const brk = free.breakMinutes === null ? autoBreak(spanMinutes(free.start, free.end, nextDay)) : free.breakMinutes;
      return {
        tempId: `p-${Date.now()}`,
attendType: 0,
shiftPatternId: null,
start: free.start,
end: free.end,
isEndNextDay: nextDay,
breakMinutes: free.breakMinutes,
businessId: null,
workOrgCode: orgCode,
memo: '',
support: null,
        workMinutes: workMinutesOf(free.start, free.end, nextDay, brk),
      };
    }
    if (sel.kind === 'pattern') {
      const p = ctl.patternMap[sel.patternId];
      if (!p || !p.start || !p.end) {
        return null;
      }
      const last = p.details[p.details.length - 1];
      const nextDay = !!(last && last.isEndNextDay);
      return {
        tempId: `p-${Date.now()}`,
attendType: 0,
shiftPatternId: p.shiftPatternId,
shiftPatternShortName: p.shortName,
color: colorOf(p.shiftPatternId).bg,
start: p.start,
end: p.end,
isEndNextDay: nextDay,
breakMinutes: null,
        businessId: (p.details.filter((d) => d.businessId)[0] || { businessId: null }).businessId,
workOrgCode: orgCode,
memo: '',
support: null,
        workMinutes: workMinutesOf(p.start, p.end, nextDay, autoBreak(spanMinutes(p.start, p.end, nextDay))),
      };
    }
    return null;
  }, [board, ctl.patternMap, orgCode, free, colorOf]);

  const sameShift = (a: SimulationShift, b: SimulationShift): boolean => {
    if (a.attendType !== b.attendType) {
      return false;
    }
    if (a.attendType !== 0) {
      return true;
    }
    if (a.shiftPatternId || b.shiftPatternId) {
      return a.shiftPatternId === b.shiftPatternId;
    }
    return a.start === b.start && a.end === b.end;
  };

  /** 選択中のものをセルに置く（同じものがあれば解除）。モックの addAssign 相当 */
  const applySelection = useCallback((staff: StaffInfo, day: DayHeader, sel: PaletteSelection) => {
    if (!board || day.locked) {
      errorNotification(day.locked ? lockMessage(day) : '');
      return;
    }
    const cell = ctl.getCell(staff.staffCode, day.date);
    const live = cell.shifts.map((s, i) => ({ s, i })).filter((x) => !x.s.deleted);
    if (sel.kind === 'erase') {
      if (live.length === 0) {
        return;
      }
      live.forEach((x) => ctl.removeFromCell(staff.staffCode, day.date, x.i));
      flash(staff.staffCode, day.date);
      successNotification(`${day.date.slice(5).replace('-', '/')} の割当を外しました`);
      return;
    }
    if (staff.isGuest && sel.kind === 'holiday') {
      errorNotification('支援スタッフには公休を登録できません。');
      return;
    }
    const cand = shiftFromSelection(sel, staff);
    if (!cand) {
      errorNotification('このパターンには時間が設定されていません。');
      return;
    }
    const dup = live.filter((x) => sameShift(x.s, cand))[0];
    if (dup) {
      ctl.removeFromCell(staff.staffCode, day.date, dup.i);
      flash(staff.staffCode, day.date);
      successNotification(`「${selectionLabel(sel)}」の割当を解除しました`);
      return;
    }
    const err = ctl.addToCell(staff.staffCode, day.date, cand);
    if (err) {
      errorNotification(err);
      return;
    }
    flash(staff.staffCode, day.date);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, ctl, shiftFromSelection, flash, errorNotification, successNotification]);

  const openCell = useCallback((staff: StaffInfo, day: DayHeader, index: number | null, presetStart?: string | null) => {
    setCellTarget({
      staff, day, index, presetStart,
    });
    setModal('cell');
  }, []);

  /** カード 1 枚の割当を外す（「割当を外す」を選んでいるときにカードを押した） */
  const eraseOne = useCallback((staff: StaffInfo, day: DayHeader, index: number) => {
    const target = ctl.getCell(staff.staffCode, day.date).shifts[index];
    if (!target || target.deleted) {
      return;
    }
    ctl.removeFromCell(staff.staffCode, day.date, index);
    flash(staff.staffCode, day.date);
    const what = target.attendType !== 0 ? '公休' : target.start ? `${target.start}–${target.end || ''}` : 'シフト';
    successNotification(`${day.date.slice(5).replace('-', '/')} の「${what}」を外しました`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctl, flash]);

  const onCellClick = useCallback((staff: StaffInfo, day: DayHeader, index: number | null) => {
    if (dragRef.current || suppressClickRef.current) {
      return;
    }
    if (staff.isGuest && ctl.getCell(staff.staffCode, day.date).shifts.filter((s) => !s.deleted).length === 0 && ctl.awayShifts(staff.staffCode, day.date).length === 0) {
      errorNotification(`${staff.belongOrgName}所属の支援スタッフです。新しいシフトの登録は所属店で行ってください`);
      return;
    }
    // カードを押したら、一括登録モードでもそのシフトの詳細を開く（9/30）。
    // 以前は選択中と同じシフトのカードを押すと割当を解除していたため、カードが 1 枚の日は詳細を開く手段がなかった。
    // 割当はセルの空いているところを押したとき。
    // ただし「割当を外す」を選んでいるときは、カードを押すとそのカードだけを外す（タブレットには右クリックが無く、詳細からしか外せなかったため。9/30）
    if (bulkMode && editable && selection && !day.locked && index !== null && selection.kind === 'erase') {
      eraseOne(staff, day, index);
      return;
    }
    if (bulkMode && editable && selection && !day.locked && index === null) {
      applySelection(staff, day, selection);
      return;
    }
    openCell(staff, day, index);
  }, [bulkMode, editable, selection, ctl, applySelection, openCell, eraseOne, errorNotification]);

  const onCellClear = useCallback((staff: StaffInfo, day: DayHeader) => {
    applySelection(staff, day, { kind: 'erase' });
  }, [applySelection]);

  // ------------------------------------------------------------------------------------------------------------ //
  // ドラッグ（パレット → セル、チップ → セル）

  const moveGhost = (x: number, y: number) => {
    const g = ghostRef.current;
    if (g) {
      g.style.left = `${x + 12}px`;
      g.style.top = `${y + 12}px`;
      g.style.opacity = '1';
    }
  };
  const hideGhost = () => {
    const g = ghostRef.current;
    if (g) {
      g.style.opacity = '0';
      g.style.left = '-9999px';
    }
  };
  const startPaletteDrag = useCallback((sel: PaletteSelection, e: React.PointerEvent) => {
    if (!editable || e.pointerType !== 'mouse') {
      return;
    }
    e.preventDefault();
    pressRef.current = { state: { kind: 'palette', sel }, x: e.clientX, y: e.clientY };
  }, [editable]);
  const startChipDrag = useCallback((staff: StaffInfo, day: DayHeader, index: number, e: React.PointerEvent) => {
    if (!editable) {
      return;
    }
    e.preventDefault();
    pressRef.current = {
      state: {
        kind: 'chip', staffCode: staff.staffCode, date: day.date, index, copy: e.shiftKey,
      },
      x: e.clientX,
      y: e.clientY,
    };
  }, [editable]);

  const endDrag = () => {
    pressRef.current = null;
    dragRef.current = null;
    hideGhost();
    setDrag(null);
    setDropKey(null);
  };

  // 文書全体のリスナーの中身。描画のたびに最新の値で差し替える（10/5）
  const pointerRef = useRef<{ move:(e: PointerEvent) => void; up: (e: PointerEvent) => void; cancel: () => void } | null>(null);
  pointerRef.current = {
    move: (e: PointerEvent) => {
      const p = pressRef.current;
      if (p && !dragRef.current) {
        if (Math.abs(e.clientX - p.x) < DRAG_START_PX && Math.abs(e.clientY - p.y) < DRAG_START_PX) {
          return;
        }
        dragRef.current = p.state;
        setDrag(p.state);
      }
      const d = dragRef.current;
      if (!d) {
        return;
      }
      moveGhost(e.clientX, e.clientY);
      if (d.kind === 'chip' && d.copy !== e.shiftKey) {
        const next = { ...d, copy: e.shiftKey };
        dragRef.current = next;
        setDrag(next);
      }
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const cell = el ? (el.closest('[data-cell]') as HTMLElement | null) : null;
      setDropKey(cell ? cell.getAttribute('data-cell') : null);
    },
    up: (e: PointerEvent) => {
      const d = dragRef.current;
      pressRef.current = null;
      if (!d) {
        // 動かさずに離した = クリック。onClick に任せる
        return;
      }
      endDrag();
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const cell = el ? (el.closest('[data-cell]') as HTMLElement | null) : null;
      if (!cell || !board) {
        return;
      }
      const [sc, date] = (cell.getAttribute('data-cell') || '').split('|');
      const staff = board.staffs.filter((x) => x.staffCode === sc)[0];
      const day = board.days.filter((x) => x.date === date)[0];
      if (!staff || !day) {
        return;
      }
      if (day.locked) {
        errorNotification(lockMessage(day));
        return;
      }
      if (d.kind === 'palette') {
        applySelection(staff, day, d.sel);
        return;
      }
      if (d.staffCode === sc && d.date === date) {
        return;
      }
      const err = ctl.moveBetweenCells({ staffCode: d.staffCode, date: d.date, index: d.index }, { staffCode: sc, date }, d.copy);
      if (err) {
        errorNotification(err);
        return;
      }
      flash(sc, date);
      successNotification(d.copy ? 'シフトをコピーしました' : 'シフトを移動しました');
    },
    cancel: endDrag,
  };

  // リスナーは 1 回だけ登録する（10/5）。以前は描画のたびに付け直しており、押した瞬間にドラッグの状態に入るので、
  // 素早いクリックの pointerup の配送中にリスナーが外れて取りこぼし、ドラッグの状態が残っていた
  // （次にセルを押すと、ドロップとクリックで割り当てが 2 回走り「追加 → 解除」になる）
  useEffect(() => {
    const move = (e: PointerEvent) => pointerRef.current && pointerRef.current.move(e);
    const up = (e: PointerEvent) => pointerRef.current && pointerRef.current.up(e);
    const cancel = () => pointerRef.current && pointerRef.current.cancel();
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', cancel);
    window.addEventListener('blur', cancel);
    return () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', cancel);
      window.removeEventListener('blur', cancel);
    };
  }, []);

  const focusCell = useCallback((staffCode: string, date: string) => {
    const el = document.querySelector(`[data-cell="${staffCode}|${date}"]`) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
    flash(staffCode, date);
  }, [flash]);

  // ------------------------------------------------------------------------------------------------------------ //
  // 各種アクション

  const goApplication = useCallback((kind: ApplicationKind) => {
    if (!cellTarget) {
      return;
    }
    const { staff, day } = cellTarget;
    sessionStorage.setItem('application.orgCode', orgCode);
    sessionStorage.setItem('application.staffCode', staff.staffCode);
    sessionStorage.setItem('application.dispStaffName', `${staff.staffCode} ${staff.staffName}`);
    sessionStorage.setItem('application.targetDate', day.date);
    sessionStorage.setItem('application.returnDestination', window.location.pathname);
    sessionStorage.setItem('attendStampList.returnDestination', window.location.pathname);
    ['application.achievementId', 'application.stampVersion', 'application.modifierApplicationId', 'application.shiftId'].forEach((k) => sessionStorage.removeItem(k));
    const ach = board ? board.achievements.filter((a) => a.staffCode === staff.staffCode && a.targetDate === day.date)[0] : null;
    if (ach) {
      sessionStorage.setItem('application.achievementId', ach.achievementId);
    }
    history.push(APPLICATION_PATH[kind]);
  }, [cellTarget, orgCode, board, history]);

  const stopRecalcPolling = useCallback(() => {
    if (recalcTimer.current !== null) {
      window.clearTimeout(recalcTimer.current);
      recalcTimer.current = null;
    }
  }, []);

  /** 再計算の進み具合を見に行く。実行中なら次の回も予約する */
  const pollRecalc = useCallback(async (jobId?: string) => {
    try {
      const r = await getRecalculateStatus(orgCode, jobId);
      setRecalcJob(r.status ? r : null);
      if (r.status === 'running') {
        recalcRunning.current = true;
        recalcTimer.current = window.setTimeout(() => pollRecalc(r.jobId || undefined), 3000);
        return;
      }
      recalcRunning.current = false;
      if (r.status === 'failed') {
        errorNotification(r.message || '人件費の再計算が途中で止まりました。確定し直すか、担当者に連絡してください。');
      } else if (r.status === 'done') {
        successNotification(r.failedCount > 0
          ? `人件費を再計算しました（${r.succeeded} 人。${r.failedCount} 人は対象外）。`
          : `人件費を再計算しました（${r.succeeded} 人）。`);
      }
      // タイマーを仕掛けた時点の ctl ではなく、最新の期間の reload を呼ぶ（その間に期間を変えても、変えた先を読み直す。10/5）
      await reloadRef.current();
      if (recalcAgain.current) {
        recalcAgain.current = false;
        // 走っている最中に確定し直した分は、サーバが順番待ちにして続けて走らせる（10/6）。画面はそれを見に行くだけ
        const next = await getRecalculateStatus(orgCode);
        if (next.status === 'running') {
          setRecalcJob(next);
          recalcRunning.current = true;
          recalcTimer.current = window.setTimeout(() => pollRecalc(next.jobId || undefined), 3000);
        }
      }
    } catch (e) {
      // 一時的に取れなくても裏の処理は続いている。次の回で拾い直す
      recalcTimer.current = window.setTimeout(() => pollRecalc(jobId), 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgCode]);

  // 画面を開いたとき・組織を変えたときに、走っている再計算があれば拾う
  useEffect(() => {
    if (!orgCode) {
      return undefined;
    }
    getRecalculateStatus(orgCode)
      .then((r) => {
        if (r.status === 'running') {
          setRecalcJob(r);
          recalcTimer.current = window.setTimeout(() => pollRecalc(r.jobId || undefined), 3000);
        }
      })
      .catch(() => undefined);
    return stopRecalcPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgCode]);

  /** 再計算の実行状況を受け取り、進み具合の表示と見回りを始める */
  const followRecalc = (job: RecalculateResult) => {
    if (job.tooMany) {
      errorNotification(`対象が ${job.maxStaff} 人を超えるため、人件費の再計算を自動で実行できませんでした。担当者に連絡してください。`);
    } else if (job.requested === 0) {
      // 前回の再計算のあと、この期間に変更が無かった。走らせる必要がない
      setRecalcJob(null);
    } else if (job.status) {
      setRecalcJob(job);
      recalcRunning.current = job.status === 'running';
      stopRecalcPolling();
      recalcTimer.current = window.setTimeout(() => pollRecalc(job.jobId || undefined), 3000);
    }
  };

  /**
   * 人件費の再計算を裏で走らせる（9/18 決定）。画面は待たない
   *
   * 確定（公開）したときはサーバが確定の API の中で始める（10/5）。ここで呼ぶのは、確定の応答に実行状況が無いとき
   * （古いサーバ）と、走っている最中に確定し直したため終わったあとにもう一度走らせるときだけ。
   * 保存では再計算しない（9/24）。
   * 走っている最中なら、終わったあとにもう一度走らせる（走り出したあとの変更は今の回に入らないため）。
   */
  const startRecalc = async () => {
    if (recalcRunning.current) {
      recalcAgain.current = true;
      return;
    }
    try {
      followRecalc(await recalculateLaborCost(orgCode, range.from, range.to, []));
    } catch (e) {
      errorNotification('人件費の再計算を開始できませんでした。シフトの保存・確定そのものは終わっています。');
    }
  };
  const doConfirm = async () => {
    setModal(null);
    setWorking(true);
    try {
      const r = await confirmShifts({ orgCode, dateFrom: range.from, dateTo: range.to });
      successNotification(r.pendingDiffTargets > 0 ? '確定しました。変更内容は翌朝の確定通知でお知らせします。' : '確定しました。通知は翌朝メールで届きます。');
      // 再計算はサーバが確定の中で始めている（10/5）。画面を閉じても再計算は漏れない。ここでは進み具合を見るだけ
      const job = r.recalculate;
      if (job && job.alreadyRunning) {
        // 前の再計算が走っていて今回の変更は入らないので、終わったあとにもう一度走らせる
        recalcAgain.current = true;
      }
      await ctl.reload();
      if (job) {
        followRecalc(job);
      } else {
        await startRecalc();
      }
    } catch (e) {
      errorNotification(describeError(e).message);
    } finally {
      setWorking(false);
    }
  };
  // ロック解除（A8 確定解除）。通知はしない。再計算も走らせない（直したあと公開したときにまとめて行う）
  const doUnconfirm = async () => {
    setModal(null);
    setWorking(true);
    try {
      await unconfirmShifts({ orgCode, dateFrom: range.from, dateTo: range.to });
      successNotification('ロックを解除しました。直したあと「確定して公開」を押してください。');
      await ctl.reload();
    } catch (e) {
      errorNotification(describeError(e).message);
    } finally {
      setWorking(false);
    }
  };

  // 保存が終わるたびに呼ばれる（9/24〜 編集はその場で保存）。
  // 人件費の再計算はここでは始めない。確定（公開）したときだけ走らせる（9/24 決定）
  useEffect(() => {
    ctl.onSaved((operations) => {
      const warnings = patternMismatches(operations);
      if (warnings.length > 0) {
        successNotification(`保存しました。別の雇用形態のパターンが含まれています。<br/>${warnings.slice(0, 5).join('<br/>')}${warnings.length > 5 ? `<br/>ほか ${warnings.length - 5} 件` : ''}`);
      }
    });
    return () => ctl.onSaved(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patternMismatches]);

  const onGenerated = (r: GeneratedOperations) => {
    setModal(null);
    ctl.applyGenerated(r.operations);
    successNotification(`${r.generated} 件をシフト表に保存しています。`);
  };
  const onPrint = async (setting: PrintSetting) => {
    setModal(null);
    if (isSimulating) {
      errorNotification('保存中です。保存が終わってからもう一度押してください。');
      return;
    }
    try {
      // 印刷の設定は自分用に、いつも最後に使ったものを覚える（9/23 に人単位へ移し、9/24 にチェックをなくした）
      await ctl.savePersonal({ printSetting: setting });
    } catch (e) {
      // 保存できなくても印刷は続ける
    }
    sessionStorage.setItem('shiftBoard.printSetting', JSON.stringify(setting));
    window.open(`/shiftBoard/print?orgCode=${encodeURIComponent(orgCode)}&dateFrom=${range.from}&dateTo=${range.to}`, '_blank');
  };
  const onSettingSaved = (saved: BoardSetting, storeSaved: boolean) => {
    setModal(null);
    successNotification('設定を保存しました。');
    // 自分の表示（開いたときの表示・刻み・時間の表記）は画面の中だけで効くので読み直さない（10/5）。
    // 以前はいつも読み直しており、週に切り替えた直後に古い期間（半月）を読み直して、盤面が半月に戻っていた
    if (storeSaved) {
      reloadRef.current();
    }
  };
  const toggleBulk = () => {
    setBulkMode(!bulkMode);
    if (!bulkMode && !selection && board && board.shiftPatterns.length > 0) {
      setSelection({ kind: 'pattern', patternId: board.shiftPatterns[0].shiftPatternId });
    }
    successNotification(bulkMode ? '一括登録モードをオフにしました' : '一括登録モードをオンにしました。パレットで選んでセルをクリックすると連続で割当できます');
  };
  const onSelectPalette = (s: PaletteSelection) => {
    setSelection(s);
    if (!bulkMode) {
      setBulkMode(true);
    }
  };

  const showPalette = !!board && editable && view !== 'day';
  // 回収枠の表示と、更新・設定のアイコン（9/17 決定）。パターンの帯を出すときは帯の見出しの行に置く（9/25）
  const rowTools = board ? (
    <React.Fragment>
      {board.period.periodId && <span>{`回収枠: ${board.period.name}（${board.period.status === 'collecting' ? '回収中' : '締切'}）`}</span>}
      <div css={styles.rowActions}>
        <button
          type="button"
          css={styles.iconAction}
          onClick={() => { ctl.reload(); successNotification('最新の状態を読み込みました。'); }}
          disabled={ctl.loading}
          title={ctl.loading ? '更新中' : '最新の状態を読み直す'}
          aria-label="更新"
        >
          <Icon type="reload" color={ctl.loading ? mock.gray40 : mock.textSub} size="16px" verticalAlign="middle" />
        </button>
        {/* 自分の表示は誰でも変えられるので、設定は編集権限が無くても開ける（9/23） */}
        <button
          type="button"
          css={styles.iconAction}
          onClick={() => setModal('settings')}
          title="設定"
          aria-label="設定"
        >
          <Icon type="settings" color={mock.textSub} size="16px" verticalAlign="middle" />
        </button>
      </div>
    </React.Fragment>
  ) : null;

  const cellShifts = cellTarget ? ctl.getCell(cellTarget.staff.staffCode, cellTarget.day.date).shifts : [];
  const editingShift = cellTarget && cellTarget.index !== null ? cellShifts[cellTarget.index] || null : null;

  return (
    <SidebarTemplate pageTitle="シフト編成">
      <BlockUI blocking={ctl.loading || working}>
        <div css={styles.page}>
          <Toolbar
            orgCode={orgCode}
            setOrgCode={ctl.setOrgCode}
            view={view}
            setView={ctl.setView}
            range={range}
            moveRange={ctl.moveRange}
            setCustomRange={ctl.setCustomRange}
            setAnchor={ctl.setAnchor}
            board={board}
            businessIds={ctl.businessIds}
            setBusinessIds={ctl.setBusinessIds}
            onAutoGenerate={() => setModal('autoGenerate')}
            onCopy={() => setModal('copy')}
            onPrint={() => setModal('print')}
            onCsv={() => downloadCsv(orgCode, range.from, range.to).catch((e) => errorNotification(describeError(e).message))}
            onRequiredStaff={() => setModal('required')}
            onAddSupport={() => setModal('support')}
            onConfirm={() => {
              if (isSimulating) {
                errorNotification('保存中です。保存が終わってからもう一度押してください。');
                return;
              }
              setModal('confirm');
            }}
            onUnlock={() => {
              if (isSimulating) {
                errorNotification('保存中です。保存が終わってからもう一度押してください。');
                return;
              }
              setModal('unconfirm');
            }}
            onSubmissions={() => history.push('/shiftBoard/submissions')}
          />
          <AlertBanners board={board} onFocusCell={focusCell} />
          {board && (
            <SimulationBar
              bulkMode={bulkMode}
              onToggleBulk={toggleBulk}
              saving={ctl.sending}
              lastSavedAt={ctl.lastSavedAt}
              editable={editable}
            />
          )}
          {recalcJob && recalcJob.status === 'running' && (
            <div css={styles.toggles}>
              <span style={{ fontWeight: 700, color: mock.primary }} title="前回の再計算のあとにシフトを変えたスタッフだけを計算します（通知の対象とは人数が違うことがあります）">人件費を再計算しています（シフトを変えた人）</span>
              <span css={styles.recalcBar}>
                <span
                  css={styles.recalcBarFill}
                  style={{ width: `${recalcJob.requested > 0 ? Math.round((recalcJob.done / recalcJob.requested) * 100) : 0}%` }}
                />
              </span>
              <span>{`${recalcJob.done} / ${recalcJob.requested} 人`}</span>
              <span>終わるまで画面はそのまま使えます。金額は終わったら自動で更新されます。</span>
            </div>
          )}
          {recalcJob && recalcJob.status !== 'running' && (recalcJob.failedCount > 0 || recalcJob.status === 'failed') && (
            <div css={styles.toggles} style={{ color: mock.secondary, fontWeight: 700 }}>
              <span>
                {recalcJob.status === 'failed'
                  ? (recalcJob.message || '人件費の再計算が途中で止まりました。確定し直してください。')
                  : `人件費を再計算しましたが、${recalcJob.failedCount} 人は計算できませんでした（${recalcJob.failedStaffCodes
                    .map((code) => (board ? (board.staffs.filter((x) => x.staffCode === code)[0] || { staffName: code }).staffName : code))
                    .join('、')}）。`}
              </span>
              <button type="button" css={mockButton} onClick={() => setRecalcJob(null)} title="この案内を閉じる">閉じる</button>
            </div>
          )}
          {/* パターンの帯を出すときは、更新・設定のアイコンを帯の見出しと同じ行に置く（9/25）。出さないときだけこの行を出す */}
          {board && !showPalette && (
            <div css={styles.toggles}>
              {rowTools}
            </div>
          )}
          <div css={styles.body}>
            {board && showPalette && (
              <PatternPalette
                patterns={board.shiftPatterns}
                colorOf={colorOf}
                distinctionHoliday={board.org.distinctionHoliday}
                selection={selection}
                onSelect={onSelectPalette}
                free={free}
                onFreeChange={setFree}
                onDragStart={startPaletteDrag}
                headerRight={rowTools}
              />
            )}
            {board && view === 'day' && (
              <DayTimeline
                board={board}
                day={board.days[0]}
                editable={editable}
                isSimulating={ctl.items !== null}
                getCell={ctl.getCell}
                awayShifts={ctl.awayShifts}
                hopeOf={hopeOf}
                colorOf={colorOf}
                onBarTap={(staff, index) => (bulkMode && editable && selection && selection.kind === 'erase' && !board.days[0].locked
                  ? eraseOne(staff, board.days[0], index)
                  : openCell(staff, board.days[0], index))}
                onLaneTap={(staff, minutes) => openCell(staff, board.days[0], null, minutesToHm(minutes))}
                onStaffTap={(staff) => { setStaffTarget(staff.staffCode); setModal('staff'); }}
                onMove={(staff, index, shift, start, end, isEndNextDay, moveType) => ctl.moveBar({
                  shiftId: shift.shiftId || '', expectedUpdateDate: shift.expectedUpdateDate || null, moveType, start, end, isEndNextDay,
                }, staff.staffCode, board.days[0].date, index).then(() => successNotification(`時間を ${start}–${end} に調整しました${shift.shiftPatternId ? '（フリーシフト）' : ''}`))}
                showLaborCost={showLaborCost}
                formatTime={ctl.formatTime}
                slotMinutes={ctl.slotMinutes}
                failedOf={ctl.failedOf}
              />
            )}
            {board && view !== 'day' && (
              <BoardGrid
                board={board}
                view={view}
                isSimulating={ctl.items !== null}
                editable={editable}
                bulkMode={bulkMode && !!selection}
                bulkHint={selectionLabel(selection)}
                getCell={ctl.getCell}
                awayShifts={ctl.awayShifts}
                hopeOf={hopeOf}
                colorOf={colorOf}
                staffSubmitted={staffSubmitted}
                onCellClick={onCellClick}
                failedOf={ctl.failedOf}
                onCellClear={onCellClear}
                onCellLongPress={(staff, day) => openCell(staff, day, null)}
                onStaffTap={(staff) => { setStaffTarget(staff.staffCode); setModal('staff'); }}
                onChipDragStart={startChipDrag}
                flashKey={flashKey}
                dropKey={dropKey}
                showLaborCost={showLaborCost}
                formatTime={ctl.formatTime}
              />
            )}
            {!board && !ctl.loading && (
              <div style={{ padding: '24px', color: mock.textSub, fontSize: '12px' }}>{ctl.loadError ? ctl.loadError.message : '組織を選択してください。'}</div>
            )}
          </div>
        </div>
      </BlockUI>

      <div ref={ghostRef} css={styles.ghost} style={drag && drag.kind === 'palette' ? { background: drag.sel.kind === 'pattern' ? colorOf(drag.sel.patternId).bg : mock.freeBg, color: drag.sel.kind === 'pattern' ? colorOf(drag.sel.patternId).fg : mock.freeText } : { background: mock.primary, color: '#fff' }}>
        {drag ? (drag.kind === 'palette' ? selectionLabel(drag.sel) : drag.copy ? 'コピー' : '移動') : ''}
      </div>

      {board && cellTarget && (
        <CellEditModal
          open={modal === 'cell'}
          board={board}
          staff={cellTarget.staff}
          day={cellTarget.day}
          shift={editingShift}
          others={cellShifts.filter((s, i) => i !== cellTarget.index && !s.deleted)}
          hope={hopeOf(cellTarget.staff.staffCode, cellTarget.day.date)}
          free={free}
          colorOf={colorOf}
          editable={editable}
          presetStart={cellTarget.presetStart}
          formatTime={ctl.formatTime}
          onClose={() => setModal(null)}
          onSave={(shift) => {
            const shifts = cellTarget.index !== null
              ? cellShifts.map((s, i) => (i === cellTarget.index ? shift : s))
              : [...cellShifts, shift];
            ctl.updateCell({ staffCode: cellTarget.staff.staffCode, targetDate: cellTarget.day.date, shifts });
            flash(cellTarget.staff.staffCode, cellTarget.day.date);
            setModal(null);
          }}
          onRemove={() => {
            if (cellTarget.index !== null) {
              ctl.removeFromCell(cellTarget.staff.staffCode, cellTarget.day.date, cellTarget.index);
              flash(cellTarget.staff.staffCode, cellTarget.day.date);
              successNotification(`${cellTarget.day.date.slice(5).replace('-', '/')} の割当を取り消しました`);
            }
            setModal(null);
          }}
          onApplication={goApplication}
        />
      )}
      <ConfirmModal
        open={modal === 'confirm'}
        orgCode={orgCode}
        dateFrom={range.from}
        dateTo={range.to}
        isReconfirm={!!board && board.period.confirmStatus !== 'NONE'}
        formatTime={ctl.formatTime}
        onClose={() => setModal(null)}
        onConfirm={doConfirm}
      />
      <UnconfirmDialog open={modal === 'unconfirm'} dateFrom={range.from} dateTo={range.to} onClose={() => setModal(null)} onUnconfirm={doUnconfirm} />
      {board && (
        <AutoGenerateModal open={modal === 'autoGenerate'} orgCode={orgCode} dateFrom={range.from} dateTo={range.to} staffs={board.staffs} onClose={() => setModal(null)} onGenerated={onGenerated} />
      )}
      {board && (
        <CopyModal open={modal === 'copy'} orgCode={orgCode} dateFrom={range.from} dateTo={range.to} staffs={board.staffs} onClose={() => setModal(null)} onGenerated={onGenerated} />
      )}
      {board && (
        <SupportStaffModal
          open={modal === 'support'}
          orgCode={orgCode}
          supportOrgs={board.supportOrgs}
          targetDate={range.from}
          onClose={() => setModal(null)}
          onAdd={(codes) => {
            ctl.setAddStaffCodes(Array.from(new Set([...ctl.addStaffCodes, ...codes])));
            setModal(null);
          }}
        />
      )}
      {board && (
        <BoardSettingsModal
          open={modal === 'settings'}
          setting={board.setting}
          editable={editable}
          onClose={() => setModal(null)}
          onSaved={onSettingSaved}
          personal={{
            ...ctl.personal,
            defaultView: ctl.personal.defaultView || board.setting.defaultView,
            slotMinutes: ctl.slotMinutes,
          }}
          onSavePersonal={async (next) => {
            await ctl.savePersonal(next);
            // 開いたときの表示を変えたら、いまの画面もそれに切り替える（保存した意図がすぐ見えるように）
            if (next.defaultView && next.defaultView !== view) {
              ctl.setView(next.defaultView);
            }
          }}
        />
      )}
      {board && (
        <PrintSettingsModal open={modal === 'print'} board={board} range={range} setting={ctl.printSetting} onClose={() => setModal(null)} onPrint={onPrint} />
      )}
      {board && (
        <RequiredStaffPanel
          open={modal === 'required'}
          board={board}
          dateFrom={range.from}
          dateTo={range.to}
          editable={editable}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); successNotification('予定人数を保存しました。'); ctl.reload(); }}
        />
      )}
      <StaffDetailPanel
        open={modal === 'staff'}
        orgCode={orgCode}
        staffCode={staffTarget}
        targetDate={range.from}
        dateFrom={range.from}
        dateTo={range.to}
        onClose={() => setModal(null)}
      />
    </SidebarTemplate>
  );
};

export default ShiftBoardPage;
